import React, { useState } from 'react';
import { Container, Card, Button, Table, Alert } from 'react-bootstrap';

const CleanupTest = () => {
  const [testResults, setTestResults] = useState(null);

  // Sample product data with empty sections (similar to what comes from PDF extraction)
  const sampleProductWithEmptySections = {
    "product_id": "1",
    "name": "Test Product",
    "product_information": {
      "Retail Pkg. UPC": "0 42238 30241 9",
      "Country of Origin": "Germany",
      "Product Type": "Each"
    },
    "nutritional_information": {}, // Empty object
    "ingredients": {}, // Empty object
    "allergens": {}, // Empty object
    "microbiological_properties": {
      "Total Plate Count": "< 10,000 CFU/g",
      "Yeast and Mold": "< 100 CFU/g"
    },
    "physical_chemical_attributes": {
      "pH": "6.5 - 7.0",
      "Moisture": "< 12%"
    }, // Has data - should be preserved
    "product_claims_certifications": {
      "CA Prop 65 Warning": "No Warning Needed"
    },
    "product_specific_fields": {}, // Empty object
    "pallet_information": {}, // Empty object
    "shipping_information": {}, // Empty object
    "validation": {
      "gtin_validation": {
        "status": "valid",
        "message": "Case UPC validated successfully"
      }
    }
  };

  // Simulate the cleanup function from DynamicForm
  const cleanupEmptySections = (product) => {
    const cleanedProduct = { ...product };
    const removedSections = [];
    
    // List of sections that should be removed if they are empty
    const sectionsToCheck = [
      'nutritional_information',
      'ingredients', 
      'allergens',
      'product_specific_fields',
      'pallet_information',
      'shipping_information',
      'logistics_information',
      'storage_handling',
      'shelf_life',
      'preparation_instructions',
      'serving_information',
      'labeling_information',
      'regulatory_compliance',
      'quality_specifications',
      'traceability_information',
      'lot_batch_information'
    ];
    
    // Always keep these essential sections even if they have data
    const essentialSections = [
      'product_information',
      'microbiological_properties',
      'physical_chemical_attributes',
      'product_claims_certifications',
      'validation'
    ];

    sectionsToCheck.forEach(sectionKey => {
      // Skip essential sections - never remove them even if empty
      if (essentialSections.includes(sectionKey)) {
        console.log(`🔒 SKIPPING ESSENTIAL SECTION: ${sectionKey} (never removed)`);
        return;
      }

      const section = cleanedProduct[sectionKey];

      // Check if section is empty (null, undefined, empty object, or object with only empty values)
      if (!section ||
          (typeof section === 'object' && Object.keys(section).length === 0) ||
          (typeof section === 'object' && Object.values(section).every(value =>
            value === '' || value === null || value === undefined
          ))) {
        delete cleanedProduct[sectionKey];
        removedSections.push(sectionKey);
      }
    });
    
    return { cleanedProduct, removedSections };
  };

  const runCleanupTest = () => {
    console.log('=== CLEANUP TEST ===');
    console.log('Original product:', sampleProductWithEmptySections);
    
    const { cleanedProduct, removedSections } = cleanupEmptySections(sampleProductWithEmptySections);
    
    console.log('Cleaned product:', cleanedProduct);
    console.log('Removed sections:', removedSections);
    
    const results = {
      original: sampleProductWithEmptySections,
      cleaned: cleanedProduct,
      removedSections: removedSections,
      originalSectionCount: Object.keys(sampleProductWithEmptySections).length,
      cleanedSectionCount: Object.keys(cleanedProduct).length,
      sectionsRemoved: removedSections.length
    };
    
    setTestResults(results);
  };

  return (
    <Container className="mt-4">
      <Card>
        <Card.Header>
          <h4>Empty Section Cleanup Test</h4>
        </Card.Header>
        <Card.Body>
          <p>This test demonstrates how empty sections are removed from products before saving to MongoDB.</p>
          
          <Button onClick={runCleanupTest} className="mb-3">
            Run Cleanup Test
          </Button>

          {testResults && (
            <div>
              <Alert variant="success">
                <h6>Cleanup Test Results</h6>
                <p>
                  <strong>Original sections:</strong> {testResults.originalSectionCount} | 
                  <strong> Cleaned sections:</strong> {testResults.cleanedSectionCount} | 
                  <strong> Removed:</strong> {testResults.sectionsRemoved}
                </p>
                {testResults.removedSections.length > 0 && (
                  <p><strong>Removed sections:</strong> {testResults.removedSections.join(', ')}</p>
                )}
              </Alert>

              <div className="row">
                <div className="col-md-6">
                  <Card className="mb-3">
                    <Card.Header>
                      <h6>Original Product (Before Cleanup)</h6>
                    </Card.Header>
                    <Card.Body>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Section</th>
                            <th>Content</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(testResults.original).map(([sectionKey, sectionData]) => (
                            <tr key={sectionKey} style={{
                              backgroundColor: testResults.removedSections.includes(sectionKey) ? '#f8d7da' : 'transparent'
                            }}>
                              <td><strong>{sectionKey}</strong></td>
                              <td>
                                {typeof sectionData === 'object' ? 
                                  (Object.keys(sectionData).length === 0 ? 
                                    <em className="text-muted">Empty {}</em> : 
                                    `${Object.keys(sectionData).length} fields`
                                  ) : 
                                  String(sectionData)
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </div>

                <div className="col-md-6">
                  <Card className="mb-3">
                    <Card.Header>
                      <h6>Cleaned Product (After Cleanup)</h6>
                    </Card.Header>
                    <Card.Body>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Section</th>
                            <th>Content</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(testResults.cleaned).map(([sectionKey, sectionData]) => (
                            <tr key={sectionKey}>
                              <td><strong>{sectionKey}</strong></td>
                              <td>
                                {typeof sectionData === 'object' ? 
                                  (Object.keys(sectionData).length === 0 ? 
                                    <em className="text-muted">Empty {}</em> : 
                                    `${Object.keys(sectionData).length} fields`
                                  ) : 
                                  String(sectionData)
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </div>
              </div>

              <div className="mt-3">
                <h6>Legend:</h6>
                <ul>
                  <li><span style={{backgroundColor: '#f8d7da', padding: '2px 4px'}}>Red</span> = Sections that will be removed</li>
                  <li>White = Sections that will be kept</li>
                </ul>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CleanupTest;
